"use client";

import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Health, Light } from "./data";

const HEALTH_STYLE: Record<Health, string> = {
    "On track": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Needs attention": "bg-amber-50 text-amber-700 border-amber-200",
    "Off track": "bg-red-50 text-red-700 border-red-200",
};

export function HealthBadge({ health }: { health: Health }) {
    return <Badge variant="outline" className={HEALTH_STYLE[health]}>{health}</Badge>;
}

export function Dot({ light }: { light: Light }) {
    const c = { green: "bg-emerald-500", amber: "bg-amber-400", red: "bg-red-500" }[light];
    return <span className={`inline-block size-2.5 shrink-0 rounded-full ${c}`} />;
}

/** The brand-coloured primary action. */
export function PrimaryButton({ className, ...props }: React.ComponentProps<typeof Button>) {
    return <Button {...props} className={cn("bg-[#007a8a] text-white hover:bg-[#006673]", className)} />;
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: "red" | "amber" | "green" }) {
    const color = tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-600" : tone === "green" ? "text-emerald-600" : "text-[#003d45]";
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-xs font-medium text-gray-500">{label}</div>
            <div className={`mt-1 text-2xl font-bold ${color}`}>{value}</div>
            {hint && <div className="mt-0.5 text-xs text-gray-400">{hint}</div>}
        </div>
    );
}

export function SectionTitle({ children, right, id }: { children: ReactNode; right?: ReactNode; id?: string }) {
    return (
        <div id={id} className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-[#003d45]">{children}</h3>
            {right}
        </div>
    );
}
